import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import EquipmentModal from '../EquipmentModal';
import parse from 'react-html-parser';

import {
  EquipmentOption,
  DetailsWrap,
  ImageHolder,
  EquipmentImage,
  SeeDetailsWrap,
} from './EquipmentCard.styles';
import { SYS_BREAKPOINT_MD, SYS_SIZE_FIXED_200 } from '@oneaudi/unified-web-common';
import { Icon, Layout, Text } from '@oneaudi/unified-web-components';

type EquipmentCardProps = {
  copy: string;
  description?: string | null;
  img?: string | null;
  id: string;
};
const EquipmentCard: React.FC<EquipmentCardProps> = ({ copy, img, description, id }) => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);
  const isMobile = useMediaQuery({ maxWidth: SYS_BREAKPOINT_MD });
  // check to see if img url contains "not available"
  const fallBackAlt = `${t('featuresAndSpecsSection.categories.accessibility.fallbackAlt')} `;

  const altText = (): string => {
    if (
      img &&
      (img.match(/((\/(image_not_available_2x_EN)))\b/g) ||
        img.match(/((\/(image_not_available_2x_FR)))\b/g))
    ) {
      return fallBackAlt;
    }
    return copy;
  };

  const noImageUrl = img ? img.includes('image_not_available') : true;

  const onKeyDownOption = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onModalOpen();
    }
    if (event.key === 'Escape') {
      onModalClose();
    }
  };

  useEffect(() => {
    if (modalOpen) {
      document.getElementById('closeModalVDPFSS')?.focus();
    } else {
      document.getElementById(id)?.focus();
    }
  }, [id, modalOpen]);

  return (
    <Layout direction={'row'}>
      {modalOpen && (
        <EquipmentModal
          copy={copy}
          image={img}
          noImageUrl={noImageUrl}
          description={description}
          alt={altText()}
          onClose={onModalClose}
        />
      )}
      <EquipmentOption
        tabIndex={0}
        onClick={onModalOpen}
        role="button"
        aria-label={t('featuresAndSpecsSection.ariaLabel.equipmentInformation')}
        id={id}
        onKeyDown={onKeyDownOption}
      >
        <Layout
          direction={{
            default: 'column',
            md: 'row',
          }}
          rowGap={SYS_SIZE_FIXED_200}
          data-testid="equipment-option-wrap"
        >
          <Text
            variant={{
              order: '1',
              type: 'copy',
              style: 'normal',
            }}
            data-testid="equipment-option-copy"
          >
            {parse(copy)}
          </Text>
          {isMobile && (
            <SeeDetailsWrap>
              <Text
                variant={{
                  order: '2',
                  type: 'copy',
                  style: 'normal',
                }}
                data-testid="view-details"
              >
                {t('featuresAndSpecsSection.categories.optionalEquipment.viewDetails')}
              </Text>
              <Icon name="forward" size="s" data-testid="chevron-right-icon" />
            </SeeDetailsWrap>
          )}
        </Layout>

        <DetailsWrap>
          {img && !noImageUrl && (
            <ImageHolder>
              <div>
                <EquipmentImage src={img} alt={altText()} data-testid="equipment-image" />
              </div>
            </ImageHolder>
          )}
        </DetailsWrap>
      </EquipmentOption>
    </Layout>
  );
};

export default EquipmentCard;
